import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Send, RefreshCw, CheckCircle2, AlertTriangle, Loader2, Plus, Trash2, Upload } from "lucide-react";
import { adamantemGet, adamantemPreview, adamantemSend, adamantemRefresh, saveReleaseData, uploadAsset, resolveAsset } from "@/lib/afmApi";
import { useAuth } from "@/context/AuthContext";

export const ADAMANTEM_STATE = {
  sending: { label: "Enviando…", cls: "border-sky-200 bg-sky-50 text-sky-700" },
  draft: { label: "Archivos pendientes", cls: "border-amber-200 bg-amber-50 text-amber-700" },
  submitted: { label: "Recibido", cls: "border-sky-200 bg-sky-50 text-sky-700" },
  in_review: { label: "En revisión", cls: "border-violet-200 bg-violet-50 text-violet-700" },
  approved: { label: "Aprobado", cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  forwarded: { label: "Enviado al proveedor", cls: "border-emerald-300 bg-emerald-100 text-emerald-800" },
  rejected: { label: "Rechazado", cls: "border-red-200 bg-red-50 text-red-700" },
  error: { label: "Error al enviar", cls: "border-red-200 bg-red-50 text-red-700" },
};

export function AdamantemBadge({ state }) {
  if (!state) return <span className="text-xs text-slate-300">—</span>;
  const s = ADAMANTEM_STATE[state] || { label: state, cls: "border-slate-200 bg-slate-50 text-slate-600" };
  return <span className={`inline-block whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${s.cls}`}>{s.label}</span>;
}

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (d?.problems) return d.problems.join(" · ");
  return typeof d === "string" ? d : "No se pudo completar la acción";
};

const emptyTrack = () => ({ title: "", isrc: "", explicit: false, audio_filename: "", audio_file_url: "", uploading: 0 });

// Para lanzamientos viejos: SONA no devuelve las canciones, así que el admin las carga aquí.
function ManualTracks({ releaseId, releaseTitle, onSaved }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([emptyTrack()]);
  const [saving, setSaving] = useState(false);
  const set = (i, k, v) => setRows((r) => r.map((x, j) => (j === i ? { ...x, [k]: v } : x)));

  const pick = async (i, file) => {
    if (!file) return;
    if (!(/\.(wav|flac)$/i.test(file.name))) { toast.error("Solo WAV o FLAC"); return; }
    set(i, "uploading", 1); set(i, "audio_filename", file.name);
    try {
      const res = await uploadAsset(user.id, file, (p) => set(i, "uploading", p));
      const u = res?.audio_file_url || res?.url || res?.path;
      set(i, "audio_file_url", resolveAsset(u)); set(i, "uploading", 100);
    } catch { set(i, "uploading", 0); set(i, "audio_filename", ""); toast.error("No se pudo subir el audio"); }
  };

  const save = async () => {
    if (rows.some((r) => !r.title.trim() || !r.audio_file_url)) { toast.error("Cada canción necesita título y audio"); return; }
    setSaving(true);
    try {
      const tracks = rows.map((r, i) => ({ position: i + 1, title: r.title.trim(), version: "", isrc: r.isrc.trim(), explicit: r.explicit, audio_filename: r.audio_filename, audio_file_url: r.audio_file_url }));
      await saveReleaseData({ release_id: releaseId, title: releaseTitle || "", tracks_json: JSON.stringify(tracks) });
      toast.success("Canciones guardadas");
      onSaved && onSaved();
    } catch (e) { toast.error(errText(e)); } finally { setSaving(false); }
  };

  const inp = "w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs outline-none focus:border-black";
  return (
    <div className="mb-3 space-y-2 rounded-md border border-slate-200 bg-white p-3" data-testid="adamantem-manual-tracks">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Cargar canciones manualmente</div>
      {rows.map((r, i) => (
        <div key={i} className="space-y-1.5 rounded-md border border-slate-100 p-2">
          <div className="flex gap-2">
            <input className={inp} placeholder={`Título canción ${i + 1}`} value={r.title} onChange={(e) => set(i, "title", e.target.value)} />
            <input className={`${inp} max-w-[140px]`} placeholder="ISRC (opcional)" value={r.isrc} onChange={(e) => set(i, "isrc", e.target.value)} />
            <button onClick={() => setRows((x) => x.filter((_, j) => j !== i))} disabled={rows.length === 1} className="text-slate-400 hover:text-red-500 disabled:opacity-30"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <label className="flex cursor-pointer items-center gap-1.5 rounded-md border border-dashed border-slate-300 px-2 py-1 hover:border-black">
              <Upload className="h-3.5 w-3.5" /> {r.audio_filename || "Audio WAV/FLAC"}
              <input type="file" accept=".wav,.flac" className="hidden" onChange={(e) => pick(i, e.target.files?.[0])} />
            </label>
            {r.uploading > 0 && r.uploading < 100 && <span className="text-slate-500">{r.uploading}%</span>}
            {r.uploading === 100 && <span className="text-emerald-600">Subido ✓</span>}
            <label className="ml-auto flex items-center gap-1 text-slate-600"><input type="checkbox" checked={r.explicit} onChange={(e) => set(i, "explicit", e.target.checked)} /> Explícito</label>
          </div>
        </div>
      ))}
      <div className="flex gap-2">
        <button onClick={() => setRows((x) => [...x, emptyTrack()])} disabled={rows.length >= 30} className="flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs hover:border-black"><Plus className="h-3.5 w-3.5" /> Añadir canción</button>
        <button onClick={save} disabled={saving || rows.some((r) => r.uploading > 0 && r.uploading < 100)} className="ml-auto rounded-md bg-black px-3 py-1 text-xs font-semibold text-white disabled:opacity-50">{saving ? "Guardando…" : "Guardar canciones"}</button>
      </div>
    </div>
  );
}

const EVENT_LABEL = {
  approved: "Aprobado por ti para enviar",
  submitted: "Recibido por la distribuidora",
  error: "Error al enviar",
  presave_ready: "Links de pre-save listos",
  store_links_ready: "Ya está en tiendas (links listos)",
};
const fmtWhen = (v) => { try { return new Date(v).toLocaleString("es-VE", { dateStyle: "medium", timeStyle: "short" }); } catch { return String(v || ""); } };
const listOf = (v) => (Array.isArray(v) ? v.filter(Boolean).join(", ") : v || "");

function Row({ k, v }) {
  if (v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length)) return null;
  return (
    <div className="flex gap-2 py-0.5">
      <span className="w-32 shrink-0 text-slate-400">{k}</span>
      <span className="min-w-0 break-words text-slate-700">{typeof v === "boolean" ? (v ? "Sí" : "No") : Array.isArray(v) ? listOf(v) : String(v)}</span>
    </div>
  );
}

// Muestra el historial, lo que tiene la distribuidora (GET /v2) o, si aún no se consultó, lo que se envió.
export function SubmissionDetail({ sub }) {
  const remote = sub?.remote && (sub.remote.metadata || sub.remote.tracks) ? sub.remote : null;
  const data = remote || sub?.sent_payload;
  const md = data?.metadata || {};
  const dist = md.distribution || {};
  const rights = md.rights || {};
  const tracks = data?.tracks || [];
  const history = [...(sub?.history || [])].reverse();
  return (
    <div className="mt-2 space-y-3 rounded-md border border-slate-200 bg-white p-3 text-[11px]" data-testid="adamantem-detail">
      <div>
        <div className="mb-1 font-semibold uppercase tracking-wider text-slate-500">Historial</div>
        {history.length ? history.map((h, i) => {
          const ev = String(h.event || "");
          const label = ev.startsWith("status:") ? `la distribuidora: ${ADAMANTEM_STATE[ev.slice(7)]?.label || ev.slice(7)}` : EVENT_LABEL[ev] || ev;
          return (
            <div key={i} className="flex gap-2 py-0.5">
              <span className="w-32 shrink-0 text-slate-400">{fmtWhen(h.at)}</span>
              <span className="text-slate-700">{label}{h.by && !ev.startsWith("status:") ? ` · ${h.by}` : ""}{h.detail ? ` — ${h.detail}` : ""}</span>
            </div>
          );
        }) : <span className="text-slate-400">Sin eventos</span>}
        {sub?.remote_checked_at && <div className="mt-1 text-slate-400">Última consulta a la distribuidora: {fmtWhen(sub.remote_checked_at)} · se revisa sola cada hora</div>}
      </div>

      {sub?.distribution && (sub.distribution.upc || Object.keys(sub.distribution.pre_save_links || {}).length > 0 || Object.keys(sub.distribution.store_links || {}).length > 0 || (sub.distribution.tracks || []).some((t) => t.isrc)) && (
        <div data-testid="adamantem-distribution">
          <div className="mb-1 font-semibold uppercase tracking-wider text-slate-500">Códigos y links (la distribuidora)</div>
          <Row k="UPC" v={sub.distribution.upc} />
          {(sub.distribution.tracks || []).filter((t) => t.isrc).map((t) => <Row key={t.index ?? t.isrc} k={`ISRC · ${t.title || t.index}`} v={t.isrc} />)}
          {[["Pre-save", sub.distribution.pre_save_links], ["Tiendas", sub.distribution.store_links]].map(([lbl, obj]) => {
            const items = Object.entries(obj || {}).filter(([, u]) => typeof u === "string" && u.startsWith("https://"));
            if (!items.length) return null;
            return (
              <div key={lbl} className="flex gap-2 py-0.5">
                <span className="w-32 shrink-0 text-slate-400">{lbl}</span>
                <span className="flex flex-wrap gap-x-3 gap-y-1">{items.map(([k, u]) => <a key={k} href={u} target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-700 underline">{k.replace(/_/g, " ")}</a>)}</span>
              </div>
            );
          })}
          {sub.distribution.updated_at && <div className="text-slate-400">Actualizado por la distribuidora: {fmtWhen(sub.distribution.updated_at)}</div>}
        </div>
      )}

      {data ? (
        <>
          <div>
            <div className="mb-1 font-semibold uppercase tracking-wider text-slate-500">{remote ? "Lo que tiene la distribuidora" : "Lo que se envió"}</div>
            <Row k="Título" v={data.title} />
            <Row k="Artista(s)" v={md.primary_artists || data.primary_artist} />
            <Row k="Featuring" v={md.featured_artists} />
            <Row k="Fecha" v={data.release_date} />
            <Row k="Tipo / formato" v={[md.submission_type === "migration" ? "Migración" : md.submission_type ? "Nuevo" : "", md.format].filter(Boolean).join(" · ")} />
            <Row k="Género" v={[md.primary_genre, md.subgenre].filter(Boolean).join(" · ")} />
            <Row k="Idioma" v={md.language} />
            <Row k="Sello" v={data.label} />
            <Row k="UPC" v={data.upc} />
            <Row k="Tiendas" v={dist.mode === "custom" ? dist.stores : dist.mode ? "Todas (global)" : ""} />
            <Row k="Territorios" v={dist.territories?.mode === "exclude" ? `Excluye: ${listOf(dist.territories?.excluded_countries)}` : dist.territories?.mode ? "Todo el mundo" : ""} />
            <Row k="© / ℗" v={[rights.c_line, rights.p_line].filter(Boolean).join("  ·  ")} />
            <Row k="Editora / PRO" v={[rights.publisher, rights.pro].filter(Boolean).join(" · ")} />
            <Row k="Precio" v={rights.digital_price_tier} />
            <Row k="Derechos aceptados" v={rights.rights_attested} />
            {md.migration && <Row k="Migración" v={`${md.migration.previous_distributor || ""} · ${md.migration.original_release_date || ""}`} />}
          </div>
          {tracks.map((t, i) => (
            <div key={i} className="rounded-md border border-slate-100 p-2">
              <div className="mb-1 font-semibold text-black">{i + 1}. {t.title}{t.version ? ` (${t.version})` : ""}{t.explicit ? " · E" : ""}</div>
              <Row k="ISRC" v={t.isrc || "Lo asigna la distribuidora"} />
              <Row k="Featuring" v={t.featured_artists} />
              <Row k="Compositores" v={t.composers} />
              <Row k="Productores" v={t.producers} />
              <Row k="Arreglistas" v={t.arrangers} />
              <Row k="TikTok desde" v={t.tiktok_preview_start} />
              <Row k="Focus track" v={t.focus_track} />
              {["performers", "studio", "label", "composition"].map((g) => (
                <Row key={g} k={{ performers: "Intérpretes", studio: "Estudio", label: "Discográfica", composition: "Composición" }[g]}
                  v={(t.contributors?.[g] || []).map((c) => (c.role ? `${c.name} (${c.role})` : c.name))} />
              ))}
            </div>
          ))}
        </>
      ) : <p className="text-slate-400">Este envío se hizo antes de guardar el detalle. Dale a "Actualizar estado" para traer lo que tiene la distribuidora.</p>}
    </div>
  );
}

export default function AdamantemPanel({ releaseId, releaseTitle, onChange }) {
  const [sub, setSub] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(false);
  const timer = useRef(null);

  const load = useCallback(() => adamantemGet(releaseId).then((d) => { setSub(d?.state ? d : null); return d; }).catch(() => null), [releaseId]);

  useEffect(() => {
    // Al abrir: trae lo último de la distribuidora (y lo copia a SONA) si el envío sigue en curso.
    load().then((d) => {
      const incomplete = d?.state === "forwarded" && !(d?.distribution?.upc && Object.keys(d?.distribution?.store_links || {}).length);
      if (["submitted", "in_review", "approved", "draft"].includes(d?.state) || incomplete) {
        adamantemRefresh(releaseId).then((x) => { if (x?.state) setSub(x); if (x?.state !== d.state) onChange && onChange(); }).catch(() => {});
      }
    });
    return () => clearTimeout(timer.current);
  }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mientras se envía, consultar cada 4 s hasta que termine.
  useEffect(() => {
    clearTimeout(timer.current);
    if (sub?.state === "sending") {
      timer.current = setTimeout(async () => {
        const d = await load();
        if (d?.state && d.state !== "sending") {
          onChange && onChange();
          if (d.state === "error") toast.error(`la distribuidora: ${d.last_error || "error al enviar"}`);
          else toast.success("Lanzamiento recibido por la distribuidora");
        }
      }, 4000);
    }
  }, [sub, load, onChange]);

  const doPreview = async () => {
    setBusy("preview"); setConfirm(false);
    try { const d = await adamantemPreview(releaseId); setPreview(d); if (d?.submission?.state) setSub(d.submission); }
    catch (e) { toast.error(errText(e)); }
    finally { setBusy(""); }
  };

  const doSend = async () => {
    setBusy("send");
    try { await adamantemSend(releaseId); setSub({ ...(sub || {}), state: "sending", last_error: null }); setConfirm(false); toast.message("Enviando a la distribuidora… puedes cerrar esta ventana"); onChange && onChange(); }
    catch (e) { toast.error(errText(e)); }
    finally { setBusy(""); }
  };

  const doRefresh = async () => {
    setBusy("refresh");
    try { const d = await adamantemRefresh(releaseId); setSub(d); onChange && onChange(); }
    catch (e) { toast.error(errText(e)); }
    finally { setBusy(""); }
  };

  const state = sub?.state;
  const canSend = !state || state === "error" || state === "draft";

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="adamantem-panel">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">Envío a la distribuidora</div>
        <AdamantemBadge state={state} />
      </div>

      {sub?.last_error && state === "error" && (
        <p className="mb-3 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">{sub.last_error}</p>
      )}
      {sub?.adamantem_id && <p className="mb-3 font-mono text-[11px] text-slate-400">ID de distribución: {sub.adamantem_id}</p>}
      {sub?.adamantem_id && (
        <details className="mb-3 text-[11px]">
          <summary className="cursor-pointer font-semibold text-slate-500" data-testid="adamantem-detail-toggle">Ver detalle del envío</summary>
          <SubmissionDetail sub={sub} />
        </details>
      )}
      {sub?.confirmation && (
        <details className="mb-3 text-[11px]" data-testid="adamantem-confirmation">
          <summary className="cursor-pointer font-semibold text-slate-500">Respuesta de confirmación</summary>
          <pre className="mt-1 max-h-40 overflow-auto rounded-md border border-slate-200 bg-white p-2 font-mono text-slate-600">{JSON.stringify({ id: sub.adamantem_id, ...sub.confirmation }, null, 2)}</pre>
          <button onClick={() => { navigator.clipboard?.writeText(JSON.stringify({ id: sub.adamantem_id, ...sub.confirmation }, null, 2)); toast.success("Copiado"); }} className="mt-1 rounded-md border border-slate-300 bg-white px-2 py-1 font-semibold hover:border-black">Copiar</button>
        </details>
      )}

      {preview && canSend && (
        <div className="mb-3 space-y-2 text-xs">
          {preview.problems?.length ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-amber-800">
              <div className="mb-1 flex items-center gap-1.5 font-semibold"><AlertTriangle className="h-3.5 w-3.5" /> Hay que corregir antes de enviar:</div>
              <ul className="list-disc space-y-0.5 pl-5">{preview.problems.map((p) => <li key={p}>{p}</li>)}</ul>
            </div>
          ) : (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-emerald-800">
              <div className="flex items-center gap-1.5 font-semibold"><CheckCircle2 className="h-3.5 w-3.5" /> Paquete listo</div>
              <div className="mt-1 text-emerald-700">
                {preview.payload.primary_artist} — {preview.payload.title} · {preview.payload.release_date} · {preview.payload.tracks.length} canción(es) · sello {preview.payload.label}
              </div>
            </div>
          )}
          {preview.problems?.some((p) => p.includes("canciones") || p.includes("audio")) && (
            <ManualTracks releaseId={releaseId} releaseTitle={releaseTitle} onSaved={doPreview} />
          )}
          {!preview.configured && <p className="text-slate-500">Falta la clave de la distribuidora en el servidor; el envío se activa cuando la configures.</p>}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {canSend && (
          <button onClick={doPreview} disabled={!!busy} data-testid="adamantem-preview" className="flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold hover:border-black disabled:opacity-50">
            {busy === "preview" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Revisar envío
          </button>
        )}
        {canSend && preview?.ok && preview?.configured && !confirm && (
          <button onClick={() => setConfirm(true)} disabled={!!busy} data-testid="adamantem-approve" className="flex items-center gap-1.5 rounded-lg bg-black px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
            <Send className="h-3.5 w-3.5" /> Aprobar y enviar
          </button>
        )}
        {confirm && (
          <>
            <button onClick={doSend} disabled={!!busy} data-testid="adamantem-confirm" className="flex items-center gap-1.5 rounded-lg bg-[#AC3333] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
              {busy === "send" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Sí, enviar a la distribuidora
            </button>
            <button onClick={() => setConfirm(false)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium hover:border-black">Cancelar</button>
          </>
        )}
        {state && !["sending", "error"].includes(state) && (
          <button onClick={doRefresh} disabled={!!busy} data-testid="adamantem-refresh" className="flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold hover:border-black disabled:opacity-50">
            <RefreshCw className={`h-3.5 w-3.5 ${busy === "refresh" ? "animate-spin" : ""}`} /> Actualizar estado
          </button>
        )}
        {state === "sending" && <span className="flex items-center gap-1.5 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Subiendo portada y audios…</span>}
      </div>
    </div>
  );
}
